import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { subscribeToPush } from '../lib/pushNotifications';
import { isCapacitor } from '../lib/platform';
import i18n from '../i18n';

const isIOS = /iPad|iPhone|iPod/.test(navigator?.userAgent);

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  const [langReady, setLangReady] = useState(false)

  const profileOperations = {
    async load(userId) {
      if (!userId) return
      setProfileLoading(true)
      try {
        const { data, error } = await supabase?.from('profiles')?.select('*')?.eq('id', userId)?.single()
        if (!error && data) {
          setUserProfile(data)
          // Resolve the actual language : profile preference, but **constrained
          // by the tenant's enabled_languages** — super_admin enforces which
          // languages are available per-tenant. If the profile has a language
          // outside that allow-list, we silently downgrade to the tenant's
          // first enabled language.
          let lang = data?.language || 'en'
          if (data?.tenant_id) {
            const { data: tenantRow } = await supabase
              ?.from('tenants')
              ?.select('enabled_languages')
              ?.eq('id', data.tenant_id)
              ?.single()
            const allowed = tenantRow?.enabled_languages
            if (Array.isArray(allowed) && allowed.length > 0 && !allowed.includes(lang)) {
              lang = allowed[0]
            }
          }
          await i18n?.changeLanguage(lang)
          // Subscribe to push notifications (non-blocking, skip on iOS — needs user gesture)
          if (!isIOS && !isCapacitor()) subscribeToPush().catch(() => {})
        }
      } catch (error) {
        console.error('Profile load error:', error)
      } finally {
        setProfileLoading(false)
        setLangReady(true)
      }
    },

    clear() {
      setUserProfile(null)
      setProfileLoading(false)
      setLangReady(false)
    }
  }

  const authStateHandlers = {
    onChange: (event, session) => {
      setUser(session?.user ?? null)
      setLoading(false)
      if (session?.user) {
        // Skip profile load for creator accounts (they don't have a public.users row)
        if (session?.user?.user_metadata?.role === 'creator') return
        profileOperations?.load(session?.user?.id)
      } else {
        profileOperations?.clear()
      }
    }
  }

  useEffect(() => {
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      if (!session) {
        // Try to recover session from storage
        const stored = window.localStorage.getItem('noirock-auth');
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            if (parsed?.refresh_token) {
              supabase?.auth?.refreshSession({ refresh_token: parsed.refresh_token })
                .then(({ data }) => {
                  authStateHandlers?.onChange(null, data?.session);
                })
                .catch(() => {
                  authStateHandlers?.onChange(null, null);
                });
              return;
            }
          } catch (e) { /* ignore parse errors */ }
        }
      }
      authStateHandlers?.onChange(null, session);
    })

    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    )

    return () => subscription?.unsubscribe();
  }, [])

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        setUser(null)
        profileOperations?.clear()
      }
      return { error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    try {
      const { data, error } = await supabase?.from('profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()
      if (!error && data) {
        setUserProfile(data)
        if (updates?.language) await i18n?.changeLanguage(updates?.language)
      }
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const setUserLang = async (lang) => {
    if (!user) return
    await supabase?.from('profiles')?.update({ language: lang })?.eq('id', user?.id)
    await i18n?.changeLanguage(lang)
    setUserProfile(prev => ({ ...prev, language: lang }))
  }

  // Activity tracking removed in NoiRock Phase 1 — profiles schema doesn't include
  // last_seen_at / last_page. Will be reintroduced via a dedicated activity table later.

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    langReady,
    signIn,
    signOut,
    updateProfile,
    setUserLang,
    isAuthenticated: !!user
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
